import { DivIcon, type LatLngTuple } from "leaflet";
import { useEffect, useState } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { Club } from "../../shared/types/club";
import { buildClubMarkerLayout } from "../../shared/utils/clubMarkerLayout";
import { resolveClubMapPosition } from "../../shared/utils/clubMapPosition";
import {
  CLUB_MAP_TILE_PROVIDERS,
  getConfiguredClubMapTileProviderKey,
} from "../../shared/utils/clubMapTileProvider";

const CLUB_MAP_TILE_PROVIDER =
  CLUB_MAP_TILE_PROVIDERS[getConfiguredClubMapTileProviderKey()];

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function buildClubIcon(club: Club) {
  const layout = buildClubMarkerLayout(club.name);
  return new DivIcon({
    className: "tnp-club-map-marker-icon",
    html: `
      <span
        class="tnp-club-map-marker tnp-club-detail-map-marker"
        aria-hidden="true"
        style="--tnp-club-marker-width:${layout.width}px;--tnp-club-marker-card-height:${layout.cardHeight}px;--tnp-club-marker-total-height:${layout.totalHeight}px;"
      >
        <span class="tnp-club-map-marker__card">
          <span class="material-symbols-outlined">location_on</span>
          <strong>${escapeHtml(club.name)}</strong>
        </span>
        <span class="tnp-club-map-marker__pin"></span>
      </span>
    `,
    iconSize: [layout.width, layout.totalHeight],
    iconAnchor: layout.iconAnchor,
    popupAnchor: layout.popupAnchor,
  });
}

function MapSizeController({
  watchKey,
}: {
  watchKey: string;
}) {
  const map = useMap();

  useEffect(() => {
    let frameId = 0;
    let timeoutId = 0;

    const invalidate = () => {
      map.invalidateSize(false);
    };

    const scheduleInvalidate = () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      frameId = window.requestAnimationFrame(() => {
        invalidate();
        timeoutId = window.setTimeout(invalidate, 240);
      });
    };

    scheduleInvalidate();

    const container = map.getContainer();
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => scheduleInvalidate());

    resizeObserver?.observe(container);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleInvalidate();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      resizeObserver?.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [map, watchKey]);

  return null;
}

export default function ClubDetailMap({ club }: { club: Club }) {
  const [position, setPosition] = useState<LatLngTuple | null>(null);
  const [resolving, setResolving] = useState(true);
  const [tilesReady, setTilesReady] = useState(false);
  const location = [club.city, club.state].filter(Boolean).join(" / ");

  useEffect(() => {
    let active = true;
    setResolving(true);
    void resolveClubMapPosition(club)
      .then((resolved) => {
        if (!active) return;
        setPosition(resolved);
      })
      .finally(() => {
        if (active) setResolving(false);
      });
    return () => {
      active = false;
    };
  }, [club]);

  useEffect(() => {
    setTilesReady(false);
  }, [club.id]);

  useEffect(() => {
    if (resolving || !position || tilesReady) return;
    const timeout = window.setTimeout(() => setTilesReady(true), 1800);
    return () => window.clearTimeout(timeout);
  }, [position, resolving, tilesReady]);

  if (resolving) {
    return (
      <div className="club-detail__map club-detail__map--leaflet tnp-club-map-shell">
        <div className="clubs-page__map-surface clubs-page__map-loading club-detail__map-loading">
          <span className="material-symbols-outlined" aria-hidden="true">
            map
          </span>
          <strong>{siteTexts.common.loading}</strong>
        </div>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="club-detail__map">
        <div className="club-detail__map-empty">
          <span className="material-symbols-outlined" aria-hidden="true">
            location_off
          </span>
          <strong>{club.name}</strong>
          <p>{[club.address, club.city, club.state].filter(Boolean).join(", ")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="club-detail__map club-detail__map--leaflet tnp-club-map-shell">
      <MapContainer
        className={`tnp-club-map club-detail__leaflet-map${tilesReady ? "" : " tnp-club-map--loading"}`}
        center={position}
        zoom={15}
        minZoom={11}
        maxZoom={18}
        zoomControl={false}
        scrollWheelZoom
        attributionControl
        aria-label={`Mapa - ${club.name}`}
      >
        <TileLayer
          attribution={CLUB_MAP_TILE_PROVIDER.attribution}
          url={CLUB_MAP_TILE_PROVIDER.url}
          maxNativeZoom={20}
          eventHandlers={{
            load: () => {
              setTilesReady(true);
            },
          }}
        />
        <MapSizeController watchKey={`${club.id}:${position[0]}:${position[1]}`} />
        <Marker position={position} icon={buildClubIcon(club)} title={club.name}>
          <Popup>
            <div className="tnp-club-map-popup">
              <strong className="tnp-club-map-popup__title">{club.name}</strong>
              {location && (
                <div className="tnp-club-map-popup__row">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    location_city
                  </span>
                  <span>{location}</span>
                </div>
              )}
            </div>
          </Popup>
        </Marker>
        <ZoomControl position="topleft" />
      </MapContainer>
    </div>
  );
}
